// Site-wide "appear on scroll" system. Works the same on every page: any
// element with the `reveal` class starts hidden (see .reveal / .reveal.
// visible in index.css) and fades/slides into place the first time it
// scrolls into view. No per-page wiring needed beyond adding the class —
// this file just finds `.reveal` elements currently in the DOM and
// observes them.
//
// Deliberately does NOT touch elements that already drive their own
// transform via JS (phone-orbit-item, scallop-tx-track, vcard-marquee-
// track, etc.) — those keep animating exactly as built. Only apply the
// `reveal` class to plain static content (headings, paragraphs, images,
// cards) you want to fade in.
let observer = null;
let scrollHooked = false;
let scrollTimer = 0;
let fallbackTimer = 0;

function isInViewport(el) {
  const rect = el.getBoundingClientRect();
  return (
    rect.bottom > 40 &&
    rect.top < window.innerHeight - 40 &&
    rect.right > 0 &&
    rect.left < window.innerWidth &&
    rect.width > 0 &&
    rect.height > 0
  );
}

function reveal(el) {
  el.classList.add('visible');
  if (observer) observer.unobserve(el);
}

function getObserver() {
  if (observer) return observer;
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // Reveal is a one-time entrance, not a re-hide-on-scroll-away
          // effect — unobserve once shown so it doesn't fight with
          // anything else moving the element later.
          reveal(entry.target);
        }
      });
    },
    { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
  );
  return observer;
}

// Force-reveal anything currently on screen. The observer covers the
// normal path; this is the backstop so a text row can never stay
// invisible when it is sitting in the viewport (e.g. callbacks that
// arrive late, or elements added to the DOM mid-scroll).
function revealOnScreen(root = document) {
  const elements = root.querySelectorAll('.reveal:not(.visible)');
  elements.forEach((el) => {
    if (isInViewport(el)) reveal(el);
  });
}

function scheduleScrollRescan() {
  if (scrollHooked) return;
  scrollHooked = true;
  // Debounced (not per-frame) so huge pages never jank mid-scroll; the
  // check runs right after scrolling settles instead.
  window.addEventListener(
    'scroll',
    () => {
      if (scrollTimer) clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        revealOnScreen();
        scanForReveals();
      }, 150);
    },
    { passive: true }
  );
}

// Call after each render/route change. Safe to call repeatedly — already-
// observed or already-visible elements are skipped.
export function scanForReveals(root = document) {
  const obs = getObserver();
  const elements = root.querySelectorAll('.reveal:not(.visible)');
  elements.forEach((el) => {
    if (el.dataset.revealObserved) return;
    el.dataset.revealObserved = 'true';
    obs.observe(el);
  });
  // Elements already in view reveal on the spot instead of waiting for
  // the observer's async delivery.
  revealOnScreen(root);
  scheduleScrollRescan();
  // Safety net: anything still hidden while on screen gets revealed.
  if (fallbackTimer) clearTimeout(fallbackTimer);
  fallbackTimer = setTimeout(() => revealOnScreen(), 2500);
}
